import { useSyncExternalStore } from "react";

// Release 1.5.0 — the live-updates connection state, one per tab. The header's sync chip reads it through
// useLiveConnectionState(); the connection manager (useLiveUpdates.connection.ts) writes it.
//   live          a push connection is open: changes arrive within a second or two, nothing polls;
//   reconnecting  the connection dropped and is being re-established; the 30-second check runs meanwhile;
//   polling       push is unavailable (blocked, refused or switched off by the server); the 30-second check keeps pages current;
//   off           live updates are not in use in this tab (release switch off, signed out, or the wall display):
//                 pages keep today's 30-second check.

export type LiveConnectionStatus = "live" | "reconnecting" | "polling" | "off";
export type LiveTransport = "webSockets" | "longPolling";

export interface LiveConnectionState {
  status: LiveConnectionStatus;
  /** How the open connection talks to the server; null unless live. */
  transport: LiveTransport | null;
  /** When the status last changed (ms since epoch). */
  since: number;
  /** When the last change was applied to this tab's pages (ms since epoch), from push or from the 30-second check. */
  lastChangeAt: number | null;
}

const INITIAL: LiveConnectionState = { status: "off", transport: null, since: Date.now(), lastChangeAt: null };

let current: LiveConnectionState = INITIAL;
const listeners = new Set<() => void>();

function emit(): void {
  if (typeof document !== "undefined") document.documentElement.dataset.liveUpdates = current.status;
  for (const listener of listeners) listener();
}

/** Moves the tab's connection status (and transport). A no-op when nothing changed. */
export function setLiveStatus(status: LiveConnectionStatus, transport: LiveTransport | null = null): void {
  const nextTransport = status === "live" ? transport : null;
  if (current.status === status && current.transport === nextTransport) return;
  current = { ...current, status, transport: nextTransport, since: Date.now() };
  emit();
}

/** Records that a change was applied to this tab's pages. */
export function markLiveChangeApplied(at: number = Date.now()): void {
  current = { ...current, lastChangeAt: at };
  emit();
}

export function getLiveConnectionState(): LiveConnectionState {
  return current;
}

export function subscribeLiveConnectionState(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test helper: back to "off" with no history. */
export function resetLiveConnectionState(): void {
  current = { ...INITIAL, since: Date.now() };
  emit();
}

/**
 * The tab's live-updates state for the header's sync chip: `live`, `reconnecting`, `polling` or `off`, the transport
 * while live, and when the last change was applied. Safe anywhere (no provider needed).
 */
export function useLiveConnectionState(): LiveConnectionState {
  return useSyncExternalStore(subscribeLiveConnectionState, getLiveConnectionState, getLiveConnectionState);
}
